/**
 * Tabs whose selection lives in the URL.
 *
 * The §8e digest deep-links to an opportunity's **Actions tab**, and that link
 * must survive the Entra round trip and render correctly as the first
 * navigation after login — so the tab cannot be component state.
 */

import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';

export interface TabDefinition {
  id: string;
  label: string;
  count?: number | undefined;
}

export function useTab(
  tabs: readonly TabDefinition[],
  param = 'tab',
): [string, (id: string) => void] {
  const [params, setParams] = useSearchParams();
  const requested = params.get(param);
  const active = tabs.some((tab) => tab.id === requested) ? requested! : (tabs[0]?.id ?? '');

  const setActive = (id: string): void => {
    const next = new URLSearchParams(params);
    next.set(param, id);
    setParams(next, { replace: true });
  };

  return [active, setActive];
}

export function TabList({
  tabs,
  active,
  onSelect,
}: {
  tabs: readonly TabDefinition[];
  active: string;
  onSelect: (id: string) => void;
}): React.ReactElement {
  return (
    <div role="tablist" aria-label="Sections" className="flex gap-1 border-b">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          id={`tab-${tab.id}`}
          aria-selected={active === tab.id}
          aria-controls={`panel-${tab.id}`}
          onClick={() => onSelect(tab.id)}
          className={cn(
            '-mb-px border-b-2 px-3 py-2 text-sm transition-colors',
            active === tab.id
              ? 'border-primary font-medium text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground',
          )}
        >
          {tab.label}
          {tab.count !== undefined && (
            <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[11px]">
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({
  id,
  active,
  children,
}: {
  id: string;
  active: string;
  children: React.ReactNode;
}): React.ReactElement | null {
  if (id !== active) return null;
  return (
    <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} className="pt-4">
      {children}
    </div>
  );
}
